// Unpublished Work © 2025 Deere & Company.
import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {v4} from 'uuid';
import {Stack, Button, Typography} from '@deere/isg.component-library';

// Triggers an artificial click to close the tooltip popup before opening the Jobs settings dialog
export async function onLinkClick(e) {
    e.preventDefault();

    try {
        document.dispatchEvent(new MouseEvent('mousedown', {
            bubbles: true
        }));
        document.dispatchEvent(new MouseEvent('mouseup', {
            bubbles: true
        }));
        document.dispatchEvent(new PointerEvent('pointerdown', {
            bubbles: true
        }));
        document.dispatchEvent(new TouchEvent('touchstart', {
            bubbles: true
        }));
    } catch {
        document.dispatchEvent(new Event('touchstart', {
            bubbles: true
        }));
    }
}

export function getSelectedJobs(jobTemplates, selectedJobIds) {
    const selectedJobs = jobTemplates.filter((job) => selectedJobIds.includes(job.id));

    return selectedJobs.sort((a, b) => alphaNumericCompare(a.title, b.title));
}

export function getTotalOperatorCount(jobs) {
    let total = 0;

    jobs.forEach((job) => {
        total += job.operatorCount ?? 0;
    });
    return total || '---';
}

export function getCutHistoryByArea(equipmentAreaId, cutHistory, CUT_HISTORY_LIMIT) {
    if (cutHistory && cutHistory[equipmentAreaId]?.length > 0) {
        return cutHistory[equipmentAreaId].slice(0, CUT_HISTORY_LIMIT);
    }
    return null;
}

export function getSelectedOperators(operators, selectedOperatorsIds) {
    const selectedOperators = operators.filter((operator) => selectedOperatorsIds.includes(operator.id));

    return selectedOperators.sort((a, b) => alphaNumericCompare(a.firstName, b.firstName));
}

export function formatJobsWithWorkItems(jobs, workItems, rowOrder, workboardFlow) {
    const workItemsIds = Object.keys(workItems);

    let formattedWorkItems = [];
    const jobsWithWorkItems = jobs.map((job, index) => {
        const matchedWorkItems = workItemsIds.filter((wi) => wi.includes(job.id));

        let seq = 0;

        if (workboardFlow === JOB) {
            seq = rowOrder.length > 0 ? rowOrder.findIndex((item) => item === job.jobTemplateId) + 1 : index + 1;
        } else {
            seq = index + 1;
        }

        if (matchedWorkItems.length > 0) {
            const fullWorkItems = matchedWorkItems.map((wiId) => {
                const newWorkItem = {
                    ...workItems[wiId],
                    appUserId: workItems[wiId].operatorId,
                    teamMember: {
                        ...workItems[wiId].teamMember,
                        compensation: {
                            ...workItems[wiId].teamMember?.onLinkUser?.userCompensation
                        }
                    },
                    jobTemplateId: job.jobTemplateId,
                    estDuration: job.estDuration || 0,
                    actualDuration: workItems[wiId].actualDuration || 0,
                    route: workItems[wiId].route?.selectedRoute,
                    routeCustomDefn: workItems[wiId].route?.routeCustomDefn
                };

                return newWorkItem;
            });

            formattedWorkItems = [...fullWorkItems];
            return {
                ...job,
                seq,
                workItems: formattedWorkItems
            };
        }
        return {
            ...job,
            seq,
            workItems: []
        };
    });
    const jobsMap = jobsWithWorkItems.reduce((jobs, job) => {
        (jobs[`${job.jobCategory}`] ??= []).push(job);
        return jobs;
    }, {});

    return {
        jobsList: jobsWithWorkItems,
        jobsMap,
        formattedWorkItems
    };
}

export function generateUserOrder(workItems, rowOrder) {
    if (rowOrder.length === 0) {
        return [];
    }

    const sortedWorkItems = [...workItems].sort((a, b) => a.seq - b.seq);

    return rowOrder.map((operatorId, index) => {
        const operatorWorkItems = sortedWorkItems.filter((wi) => wi.operatorId === operatorId);
        const userId = operatorWorkItems.length > 0 ? operatorWorkItems[0].teamMember.id : operatorId;

        return {
            seq: index + 1,
            userId,
            workItems: operatorWorkItems.map((wi) => ({
                seq: wi.seq,
                workItemId: wi.workItemId
            }))
        };
    });
}

export function generateWorkItemMap(workItems) {
    const workItemObj = {};

    let id = '';

    for (const item of workItems) {
        id = `${item.jobId}::${item.operatorId}`;
        workItemObj[id] = {
            ...item,
            operatorId: item.operatorId,
            workItemId: v4()
        };
    }
    return workItemObj;
}

function compareWorkItems(prevWorkItem, newWorkItem) {
    return Object.keys(prevWorkItem).some((key) => {
        if (['seq', 'actualDuration', 'jobId', 'operatorId', 'note'].includes(key)) {
            return prevWorkItem[key] !== newWorkItem[key];
        }

        if (key === 'equipment') {
            if (prevWorkItem[key].length !== newWorkItem[key].length) {
                return true;
            }

            return prevWorkItem[key].some((eq, id) => eq.equipmentId !== newWorkItem[key][id].equipmentId);
        }

        if (key === 'route') {
            return [
                prevWorkItem[key].selectedRoute !== newWorkItem[key].selectedRoute,
                prevWorkItem[key].routeCustomDefn.length !== newWorkItem[key].routeCustomDefn.length,
                prevWorkItem[key].routeCustomDefn.some((custom, id) => custom !== newWorkItem[key].routeCustomDefn[id])
            ].some((v) => v === true);
        }

        return false;
    });
}

function compareDetails(prevWorkboard, newWorkboard) {
    return Object.keys(prevWorkboard.details).some((attr) => prevWorkboard.details[attr] !== newWorkboard.details[attr]);
}

function compareJobs(prevWorkboard, newWorkboard) {
    if (prevWorkboard.jobs.length !== newWorkboard.jobs.length) {
        return true;
    }

    return prevWorkboard.jobs.some((job, index) => job.jobTemplateId !== newWorkboard.jobs[index].jobTemplateId);
}

function compareOperators(prevWorkboard, newWorkboard) {
    if (prevWorkboard.operators.length !== newWorkboard.operators.length) {
        return true;
    }

    return prevWorkboard.operators.some((op, index) => op.id !== newWorkboard.operators[index].id);
}

function compareCutsAndDirections(prevWorkboard, newWorkboard) {
    if (prevWorkboard.cutsAndDirections.length !== newWorkboard.cutsAndDirections.length) {
        return true;
    }

    return prevWorkboard.cutsAndDirections.some(
        (cut, index) => Object.keys(cut).some((v) => cut[v] !== newWorkboard.cutsAndDirections[index][v])
    );
}

function compareRowOrder(prevWorkboard, newWorkboard) {
    if (prevWorkboard.rowOrder.length !== newWorkboard.rowOrder.length) {
        return true;
    }

    return prevWorkboard.rowOrder.some((item, index) => item !== newWorkboard.rowOrder[index]);
}

function compareWorkItemsMap(prevWorkboard, newWorkboard) {
    if (Object.keys(prevWorkboard.workItems).length !== Object.keys(newWorkboard.workItems).length) {
        return true;
    }

    return Object.keys(prevWorkboard.workItems).some((id) => compareWorkItems(prevWorkboard.workItems[id], newWorkboard.workItems[id]));
}

const compareWorkboardKeyMap = {
    details: compareDetails,
    jobs: compareJobs,
    operators: compareOperators,
    cutsAndDirections: compareCutsAndDirections,
    rowOrder: compareRowOrder,
    workItems: compareWorkItemsMap
};

export function compareWorkboards(prevWorkboard, newWorkboard) {
    return Object.keys(prevWorkboard).some((key) => {
        const compareByKey = compareWorkboardKeyMap[key];

        if (!compareByKey) {
            return false;
        }

        return compareByKey(prevWorkboard, newWorkboard);
    });
}

export function SelectAllConfirmation({
    title,
    onCancel,
    onConfirm,
    translations
}) {
    return (
        <Stack
            alignItems='center'
            direction='row'
            paddingInline='8px'
            spacing={1}
        >
            <Typography
                sx={{
                    flex: 1
                }}
                variant='body'
            >
                {title}
            </Typography>
            <Button
                onClick={onCancel}
                variant='secondary'
            >
                {translations.CANCEL}
            </Button>
            <Button
                onClick={onConfirm}
                variant='primary'
            >
                {translations.confirm}
            </Button>
        </Stack>
    );
}
SelectAllConfirmation.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    translations: PropTypes.object.isRequired
};
